import React, { useState } from 'react'
import { isEmpty } from 'lodash'
import { FormField } from '@/components/forms/FormField'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { TextInput } from '@/components/forms/TextInput'
import { TextArea } from '@/components/forms/TextArea'
import { ErrorMessage } from '@/components/forms/ErrorMessage'

const VAR_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/

const AddVariableModal = ({ show, busy, existingNames, onClose, onSubmit }) => {
  const [name, setName] = useState('')
  const [defaultValue, setDefaultValue] = useState('')
  const [description, setDescription] = useState('')

  const trimmedName = name.trim()
  const collides = existingNames.includes(trimmedName)
  const malformed = !isEmpty(trimmedName) && !VAR_NAME_RE.test(trimmedName)

  let validationError = null
  if (malformed) {
    validationError =
      'Name must start with a letter or underscore and contain only letters, digits, and underscores.'
  } else if (collides) {
    validationError = `A variable named "${trimmedName}" already exists.`
  }

  const canSubmit =
    !busy && !isEmpty(trimmedName) && !isEmpty(defaultValue.trim()) && !validationError

  const submit = () => {
    if (canSubmit) {
      onSubmit({ name: trimmedName, defaultValue, description: description.trim() })
    }
  }

  return (
    <BasicModal
      show={show}
      onClose={onClose}
      title="Add variable"
      size="small"
      footer={
        <div className="form-actions">
          <Button onClick={onClose} data-testid="add-variable-cancel">
            Cancel
          </Button>
          <Button brand onClick={submit} disabled={!canSubmit} data-testid="add-variable-submit">
            {busy ? 'Adding…' : 'Add variable'}
          </Button>
        </div>
      }
    >
      <div className="voice-agents__add-variable-form" data-testid="add-variable-form">
        <FormField>
          <label htmlFor="add-variable-name">Name</label>
          <TextInput
            small
            id="add-variable-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="customer_name"
            data-testid="add-variable-name"
            autoFocus
          />
          <span className="voice-agents__add-variable-hint">
            {/* eslint-disable-next-line no-template-curly-in-string */}
            Reference it in prompts as <code>{'${name}'}</code>.
          </span>
        </FormField>

        <FormField>
          <label htmlFor="add-variable-default-value">Default value</label>
          <TextArea
            id="add-variable-default-value"
            value={defaultValue}
            onChange={(event) => setDefaultValue(event.target.value)}
            rows={3}
            placeholder="The value used when no override is set"
            data-testid="add-variable-default-value"
          />
        </FormField>

        <FormField>
          <label htmlFor="add-variable-description">
            Description <span className="voice-agents__add-variable-optional">(optional)</span>
          </label>
          <TextArea
            id="add-variable-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={2}
            placeholder="What this variable is for"
            data-testid="add-variable-description"
          />
        </FormField>

        {validationError && (
          <ErrorMessage content={validationError} data-testid="add-variable-error" />
        )}
      </div>
    </BasicModal>
  )
}

export default AddVariableModal
